import 'core-js/stable'
import 'regenerator-runtime/runtime'
import React from 'react'
import UniversalRouter from 'universal-router'
import queryString from 'query-string'
import history from './core/history'
import App from './common/App'
import { ErrorReporter, deepForceUpdate } from './core/devUtils'
import { configureStore } from '@reduxjs/toolkit'
import { Provider } from 'react-redux'
import reducers from './core/reducers'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { toast, ToastContainer } from 'react-toastify'
import Transition from 'react-transition-group/Transition'
import './common/style.scss'
//import 'bootstrap-sass/assets/stylesheets/_bootstrap.scss';
import './css/bootstrap.scss'
import './css/sharp-star.css';
import { hydrateRoot } from 'react-dom/client'
import 'react-toastify/dist/ReactToastify.css'

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    const removeCss = styles.map(style => {
      if (style && typeof style._insertCss === 'function') {
        return style._insertCss()
      }
      return () => {}
    })
    return () => removeCss.forEach(dispose => dispose())
  }
}

function updateTag (tagName, keyName, keyValue, attrName, attrValue) {
  const node = document.head.querySelector(`${tagName}[${keyName}="${keyValue}"]`)
  if (node && node.getAttribute(attrName) === attrValue) return

  // Remove and create a new tag in order to make it work with bookmarks in Safari
  if (node) {
    node.parentNode.removeChild(node)
  }
  if (typeof attrValue === 'string') {
    const nextNode = document.createElement(tagName)
    nextNode.setAttribute(keyName, keyValue)
    nextNode.setAttribute(attrName, attrValue)
    document.head.appendChild(nextNode)
  }
}
function updateMeta (name, content) {
  updateTag('meta', 'name', name, 'content', content)
}
function updateCustomMeta (property, content) { // eslint-disable-line no-unused-vars
  updateTag('meta', 'property', property, 'content', content)
}
function updateLink (rel, href) { // eslint-disable-line no-unused-vars
  updateTag('link', 'rel', rel, 'href', href)
}

// Switch off the native scroll restoration behavior and handle it manually
// https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
const scrollPositionsHistory = {}
if (window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual'
}

let onRenderComplete = function initialRenderComplete (route, location) {
  const elem = document.getElementById('css')
  if (elem) elem.parentNode.removeChild(elem)
  // Set the title on the initial render too: the SSR-rendered title can be wrong (e.g. the brief
  // notFound 'Page Not Found' when a /find-an-ifa-prefixed URL is opened directly on UAT).
  if (route) document.title = route.title
  onRenderComplete = function renderComplete (route, location) {
    document.title = route.title

    updateMeta('description', route.description)
    // Update necessary tags in <head> at runtime here, ie:
    // updateMeta('keywords', route.keywords);
    // updateCustomMeta('og:url', route.canonicalUrl);
    // updateCustomMeta('og:image', route.imageUrl);
    // updateLink('canonical', route.canonicalUrl);
    // etc.

    let scrollX = 0
    let scrollY = 0
    const pos = scrollPositionsHistory[location.key]
    if (pos) {
      scrollX = pos.scrollX
      scrollY = pos.scrollY
    } else {
      const targetHash = location.hash.substr(1)
      if (targetHash) {
        const target = document.getElementById(targetHash)
        if (target) {
          scrollY = window.pageYOffset + target.getBoundingClientRect().top
        }
      }
    }

    // Restore the scroll position if it was saved into the state
    // or scroll to the given #hash anchor
    // or scroll to top of the page
    window.scrollTo(scrollX, scrollY)

    // Google Analytics tracking. Don't send 'pageview' event after
    // the initial rendering, as it was already sent
    if (window.ga) {
      window.ga('send', 'pageview', location.pathname + location.search + location.hash)
    }
  }
}

// Make taps on links and buttons work fast on mobiles
// FastClick.attach(document.body)

const ZoomInAndOut = ({ children, position, ...props }) => (
  <Transition
    {...props}
    {...500}
    timeout={800}
    onEnter={node => node.classList.add('zoomIn', 'animate')}
    onExit={node => {
      node.classList.remove('zoomIn', 'animate')
      node.classList.add('zoomOut', 'animate')
    }}
  >
    {children}
  </Transition>
)

const container = document.getElementById('app')
let appInstance
let currentLocation = history.location
let routes = require('./routes').default

// Create store ONCE, outside of onLocationChange, with preloaded state from server
const preloadedState = window.__INITIAL_STATE__ || {}
const store = configureStore({
  reducer: reducers,
  preloadedState,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware(),
  devTools: process.env.NODE_ENV !== 'production'
})

// Re-render the app when window.location changes
async function onLocationChange (location) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset
  }
  // Delete stored scroll position for next page if any
  if (history.action === 'PUSH') {
    delete scrollPositionsHistory[location.key]
  }
  currentLocation = location

  try {
    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const router = new UniversalRouter(routes)
    const route = await router.resolve({
      pathname: location.pathname,  // ← Changed from 'path' to 'pathname'
      query: queryString.parse(location.search),
      store: store
    })

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return
    }

    if (route.redirect) {
      history.replace(route.redirect)
      return
    }

    const container = document.getElementById('app')

    if (!container._reactRoot) {
      container._reactRoot = hydrateRoot(
        container,
        <>
        <Provider store={store}>
          <App context={context}>
            {route.component}
          </App>
        </Provider>
        <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
            pauseOnHover
          />
        </>
      )
    } else {
      container._reactRoot.render(
        <>
          <Provider store={store}>
            <App context={context}>
              {route.component}
            </App>
          </Provider>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </>
      )
    }

    // React 18's hydrateRoot/root.render take no completion callback (the pre-migration
    // ReactDOM.render did). Call it explicitly so per-screen <title> updates fire on every
    // navigation, and the initial render corrects a wrong SSR title.
    Promise.resolve().then(() => onRenderComplete(route, location))

  } catch (error) {
    console.error(error) // eslint-disable-line no-console

    // Current url has been changed during navigation process, do nothing
    if (currentLocation.key !== location.key) {
      return
    }

    // Display the error in full-screen for development mode
    if (process.env.NODE_ENV !== 'production') {
      appInstance = null
      document.title = `Error: ${error.message}`
      // Do NOT call hydrateRoot again — the container already has a root (created on
      // first load). Re-hydrating throws a hydration error, which re-fires this path
      // and loops. Render the error via the existing root instead.
      if (container._reactRoot) {
        container._reactRoot.render(<ErrorReporter error={error} />)
      }
      return
    }

    // Avoid broken navigation in production mode by a full page reload on error
    window.location.reload()
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange)
onLocationChange(currentLocation)

// Handle errors that might happen after rendering
// Display the error in full-screen for development mode
if (process.env.NODE_ENV !== 'production') {
  window.addEventListener('error', (e) => {
    console.error(e.error) // eslint-disable-line no-console

    document.title = `Error: ${e.error && e.error.message}`
    // Intentionally do NOT re-render here. Calling hydrateRoot on the already-rooted
    // container threw a hydration error, which re-fired this handler and looped —
    // turning a single (often benign) error into ~10k errors that bricked the page.
  })
}

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./routes', () => {
    routes = require('./routes').default // eslint-disable-line global-require

    if (appInstance) {
      try {
        // Force-update the whole tree, including components that refuse to update
        deepForceUpdate(appInstance)
      } catch (error) {
        appInstance = null
        document.title = `Hot Update Error: ${error.message}`
        console.log('Error3: ', error.message)
        hydrateRoot(container, <ErrorReporter error={error} />)
        return
      }
    }

    onLocationChange(currentLocation)
  })
}

// Only boot App Insights outside local development (and only when a key is configured). In dev
// the adblocker blocks its telemetry POSTs, spamming the console with ERR_BLOCKED_BY_CLIENT.
if (process.env.NODE_ENV !== 'development' && process.env.APPINSIGHTS_INSTRUMENTATIONKEY) {
  const reactPlugin = new ReactPlugin()

  const appInsights = new ApplicationInsights({
    config: {
      // appId: "FAIFA-React-App",
      instrumentationKey: process.env.APPINSIGHTS_INSTRUMENTATIONKEY,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: { history: history }
      }
      /* ...Other Configuration Options... */
    }
  })
  appInsights.loadAppInsights()
}


